import { type Editor } from '@tiptap/react';
import { Box, Quote } from 'lucide-react';
import { useCallback, useMemo, useState, type ComponentType } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { BlockQuoteIcon } from '@/components/tiptap-editor/tiptap-icons/block-quote-icon';
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- Lib ---
import { isNodeInSchema } from '@/lib/tiptap-utils';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';

export interface BlockquoteOption {
    type: string;
    label: string;
    icon: ComponentType<{ className?: string }>;
    command: (editor: Editor) => void;
    canExecute?: (editor: Editor) => boolean;
}

export const blockquoteOptions: BlockquoteOption[] = [
    {
        type: 'blockquote',
        label: 'Blockquote',
        icon: Quote,
        command: (editor: Editor) => {
            editor.chain().focus().toggleWrap('blockquote').run();
        },
        canExecute: (editor: Editor) => {
            return editor.can().toggleWrap('blockquote');
        },
    },
    {
        type: 'zyCitation',
        label: 'zyCitation',
        icon: Box,
        command: (editor: Editor) => {
            editor.commands.insertZyCitation();
        },
        canExecute: (editor: Editor) => {
            return editor.isActive('blockquote');
        },
    },
];

const BLOCKQUOTE_SHORTCUT_KEYS: Record<string, string> = {
    blockquote: 'mod+shift+b',
};

export interface BlockquoteMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor;

    onOpenChange?: (isOpen: boolean) => void;
}

export function shouldShowBlockquoteMenu({ editor, hasNodes }: { editor: Editor | null; hasNodes: boolean }): boolean {
    if (!hasNodes || !editor) {
        return false;
    }
    return true;
}

export function useBlockquoteMenuState(editor: Editor | null) {
    const [isOpen, setIsOpen] = useState(false);

    const blockquoteInSchema = isNodeInSchema('blockquote', editor);
    const zyCitationInSchema = isNodeInSchema('zyCitation', editor);
    const hasNodes = blockquoteInSchema && zyCitationInSchema;

    const handleOpenChange = (open: boolean, callback?: (isOpen: boolean) => void) => {
        setIsOpen(open);
        callback?.(open);
    };

    return {
        isOpen,
        setIsOpen,
        hasNodes,
        handleOpenChange,
    };
}

export function BlockquoteMenu({ editor: providedEditor, onOpenChange, ...props }: BlockquoteMenuProps) {
    const { editor } = useTiptapEditor(providedEditor);

    const { isOpen, hasNodes, handleOpenChange } = useBlockquoteMenuState(editor);

    const show = useMemo(() => {
        return shouldShowBlockquoteMenu({
            editor,
            hasNodes,
        });
    }, [editor, hasNodes]);

    const handleOnOpenChange = useCallback((open: boolean) => handleOpenChange(open, onOpenChange), [handleOpenChange, onOpenChange]);

    const handleOptionClick = useCallback(
        (option: BlockquoteOption) => {
            if (!editor) return;
            option.command(editor);
            handleOpenChange(false, onOpenChange);
        },
        [editor, handleOpenChange, onOpenChange],
    );

    if (!show || !editor || !editor.isEditable) {
        return null;
    }

    return (
        <DropdownMenu
            open={isOpen}
            onOpenChange={handleOnOpenChange}
        >
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    data-style="ghost"
                    data-active-state="off"
                    role="button"
                    tabIndex={-1}
                    aria-label="Insert Blockquote"
                    tooltip="Blockquote"
                    {...props}
                >
                    <BlockQuoteIcon className="tiptap-button-icon" />
                    <ChevronDownIcon className="tiptap-button-dropdown-small" />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
                <Card>
                    <CardBody>
                        <DropdownMenuGroup>
                            {blockquoteOptions.map(option => {
                                const isOptionDisabled = !option.canExecute?.(editor);
                                return (
                                    <DropdownMenuItem
                                        key={option.type}
                                        asChild
                                        disabled={isOptionDisabled}
                                    >
                                        <Button
                                            type="button"
                                            data-style="ghost"
                                            role="button"
                                            tabIndex={-1}
                                            disabled={isOptionDisabled}
                                            onClick={() => handleOptionClick(option)}
                                            tooltip={option.label}
                                            shortcutKeys={BLOCKQUOTE_SHORTCUT_KEYS[option.type]}
                                        >
                                            <div className="flex-start flex w-full">
                                                <option.icon className="mr-2 h-4 w-4" />
                                                <span>{option.label}</span>
                                            </div>
                                        </Button>
                                    </DropdownMenuItem>
                                );
                            })}
                        </DropdownMenuGroup>
                    </CardBody>
                </Card>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default BlockquoteMenu;
